import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useState, useEffect } from 'react';

// Digital scramble characters
const CHARS = '01アイウエオカキクケコ';

function useScrambleText(text: string, isScrambling: boolean) {
  const [display, setDisplay] = useState(text);
  
  useEffect(() => {
    if (!isScrambling) {
      setDisplay(text);
      return;
    }
    
    let frame = 0;
    const totalFrames = 12;
    
    const interval = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      
      setDisplay(
        text
          .split('')
          .map((char, index) => {
            if (progress > (index / text.length) + 0.3) {
              return char;
            }
            return CHARS[Math.floor(Math.random() * CHARS.length)];
          })
          .join('')
      );
      
      if (frame >= totalFrames) {
        clearInterval(interval);
        setDisplay(text);
      }
    }, 40);
    
    return () => clearInterval(interval);
  }, [text, isScrambling]);
  
  return display;
}

export function LanguageToggle() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayLang, setDisplayLang] = useState(currentLang);
  
  const scrambledLang = useScrambleText(displayLang.toUpperCase(), isTransitioning);

  const toggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'es' : 'en';
    setIsTransitioning(true);
    
    setTimeout(() => {
      // changeLanguage triggers LanguageDetector to persist to localStorage (bts_lang)
      i18n.changeLanguage(newLang);
      setDisplayLang(newLang);
    }, 150);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  // Sync display with actual language
  useEffect(() => {
    if (!isTransitioning) {
      setDisplayLang(currentLang);
    }
  }, [currentLang, isTransitioning]);

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={toggleLanguage}
      className="relative flex items-center gap-2 px-3 py-2 rounded-lg
                 bg-brand-blue/60 hover:bg-brand-lightBlue/80 
                 border border-brand-lightBlue/30 hover:border-brand-yellow/30
                 text-dark-300 hover:text-brand-yellow
                 transition-all duration-200 backdrop-blur-sm
                 overflow-hidden"
    >
      {/* Animated background on transition */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-brand-yellow/20 via-brand-yellow/10 to-brand-yellow/20"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'linear' }}
          />
        )}
      </AnimatePresence>
      
      {/* Globe icon with rotation on transition */}
      <motion.div
        animate={{ 
          rotate: isTransitioning ? 360 : 0,
          scale: isTransitioning ? [1, 1.2, 1] : 1
        }}
        transition={{ duration: 0.5 }}
      >
        <Globe className="w-4 h-4" />
      </motion.div>
      
      {/* Language code with digital effect */}
      <span className="text-sm font-mono font-medium w-5 text-center">
        {scrambledLang}
      </span>
      
      {/* Glitch effect layers during transition */}
      <AnimatePresence>
        {isTransitioning && (
          <>
            <motion.span
              className="absolute right-3 text-sm font-mono font-medium text-brand-yellow opacity-70"
              style={{ clipPath: 'inset(0 0 50% 0)' }}
              initial={{ x: 0 }}
              animate={{ x: [-2, 2, -1, 1, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {scrambledLang}
            </motion.span>
            <motion.span
              className="absolute right-3 text-sm font-mono font-medium text-accent-cyan opacity-70"
              style={{ clipPath: 'inset(50% 0 0 0)' }}
              initial={{ x: 0 }}
              animate={{ x: [2, -2, 1, -1, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {scrambledLang}
            </motion.span>
          </>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
